import type { NotificationDto } from '@schwrz/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { api } from '../shared/api/client';

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<NotificationDto[]>([]);
  const unreadCount = ref(0);
  const loading = ref(false);

  async function loadList(): Promise<void> {
    loading.value = true;
    try {
      const res = await api<{ notifications: NotificationDto[]; unreadCount: number }>(
        '/api/notifications',
      );
      items.value = res.notifications;
      unreadCount.value = res.unreadCount;
    } finally {
      loading.value = false;
    }
  }

  async function refreshUnreadCount(): Promise<void> {
    try {
      const res = await api<{ unreadCount: number }>('/api/notifications/unread-count');
      unreadCount.value = res.unreadCount;
    } catch {
      // Silently ignore polling errors — network may be unavailable.
    }
  }

  async function markAllRead(): Promise<void> {
    const res = await api<{ unreadCount: number }>('/api/notifications/read-all', {
      method: 'POST',
    });
    unreadCount.value = res.unreadCount;
    const now = new Date().toISOString();
    for (const item of items.value) {
      if (!item.readAt) item.readAt = now;
    }
  }

  async function markRead(id: string): Promise<void> {
    await api<{ ok: true }>(`/api/notifications/${id}/read`, { method: 'POST' });
    const item = items.value.find((n) => n.id === id);
    if (item && !item.readAt) {
      item.readAt = new Date().toISOString();
      if (unreadCount.value > 0) unreadCount.value -= 1;
    }
  }

  async function clearRead(): Promise<void> {
    await api<{ ok: true }>('/api/notifications/clear-read', { method: 'POST' });
    items.value = items.value.filter((n) => !n.readAt);
  }

  return {
    items,
    unreadCount,
    loading,
    loadList,
    refreshUnreadCount,
    markAllRead,
    markRead,
    clearRead,
  };
});
